/**
 * Maps FHIR QuestionnaireResponse and Questionnaire to supervision queue display.
 *
 * Status: QuestionnaireResponse has .status (in-progress | completed | amended | …).
 * It does NOT have .subjectType (that is on Questionnaire). Our table status
 * (DocumentReviewStatus) is derived from QR.status plus provider/supervisor signature.
 *
 * Document type: NOT on QuestionnaireResponse.subject or .subjectType. It comes from
 * the referenced Questionnaire’s meta.tag (template-category system). Resolve
 * QuestionnaireResponse.questionnaire to get the template and read its category.
 */

import type { Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import {
  getRequestedEditsFromQR,
  getSignatureFromQR,
  PROVIDER_SIGNATURE_EXT_URL,
  SUPERVISOR_SIGNATURE_EXT_URL,
} from '../../appointments/fhirNoteMapping';
import { getTemplateCategory } from '../../settings/template-library/utils/templateTags';
import type { TemplateCategory } from '../../settings/template-library/types';
import type { Role } from '../../../pages/users/types/rbac';
import type { DocumentReviewStatus, DocumentType, MissingDocumentFormType } from '../types';

/** 1:1 map from Template Library category to supervision document type. */
const TEMPLATE_CATEGORY_TO_DOCUMENT_TYPE: Record<TemplateCategory, DocumentType> = {
  appointments: 'notes',
  measures: 'assessments',
  'treatment-planning': 'treatment-planning',
  forms: 'forms',
  administrative: 'other',
};

/**
 * Map FHIR QuestionnaireResponse to supervision table status using canonical terms.
 * cosigned = supervisor has co-signed (note locked). signed = provider signed, awaiting supervisor.
 * edits-requested = supervisor requested edits (provider sig removed). draft = in progress or not yet signed.
 */
export function qrToDocumentReviewStatus(qr: QuestionnaireResponse): DocumentReviewStatus {
  const hasSupervisorSig = !!getSignatureFromQR(qr, SUPERVISOR_SIGNATURE_EXT_URL);
  const hasProviderSig = !!getSignatureFromQR(qr, PROVIDER_SIGNATURE_EXT_URL);
  const requestedEdits = getRequestedEditsFromQR(qr);

  if (hasSupervisorSig) return 'cosigned';
  if (hasProviderSig) return 'signed';
  if (requestedEdits) return 'edits-requested';
  if (qr.status === 'amended') return 'amended';
  if (qr.status === 'in-progress') return 'draft';
  return 'draft';
}

/** Supervision notes queue: role-default (cosign / provider unsigned) vs supervisor's own drafts. */
export type SupervisionNotesQueue = 'role-default' | 'own-unsigned';

/** Statuses for a notes queue. Own-unsigned is drafts only (supervisors are not supervised). */
export function getNotesQueueStatuses(
  role: Role,
  queue: SupervisionNotesQueue = 'role-default',
): DocumentReviewStatus[] {
  if (queue === 'own-unsigned') return ['draft'];
  if (role === 'supervisor') return ['signed', 'edits-requested'];
  if (role === 'provider') return ['draft', 'edits-requested'];
  return [];
}

/** Types that belong on Unsigned Notes / My Unsigned Notes (session notes + treatment plans). */
export const OWN_UNSIGNED_DOCUMENT_TYPES: DocumentType[] = ['notes', 'treatment-planning'];

export function isOwnUnsignedNotesQueue(role: Role, queue: SupervisionNotesQueue): boolean {
  return queue === 'own-unsigned' || (queue === 'role-default' && role === 'provider');
}

/**
 * Get document type from the Questionnaire (template). Document type is NOT
 * QuestionnaireResponse.subject or .subjectType; it is stored on the
 * Questionnaire as meta.tag with system TEMPLATE_CATEGORY_SYSTEM.
 * Maps to cosign table types: Assessments, Forms, Notes, Other.
 *
 * Falls back to title-based heuristic when the template has no category tag
 * (e.g. templates created outside the Template Library).
 */
export function questionnaireToDocumentType(questionnaire: Questionnaire | null): DocumentType {
  if (!questionnaire) return 'other';
  const cat = getTemplateCategory(questionnaire);
  if (cat) return TEMPLATE_CATEGORY_TO_DOCUMENT_TYPE[cat] ?? 'other';

  const title = (questionnaire.title ?? questionnaire.name ?? '').toLowerCase();
  if (title.includes('progress') || title.includes('note') || title.includes('soap') || title.includes('intake') || title.includes('session')) {
    return 'notes';
  }
  if (title.includes('assessment') || title.includes('phq') || title.includes('gad') || title.includes('measure')) {
    return 'assessments';
  }
  if (title.includes('treatment plan') || title.includes('tx plan') || title.includes('diagnosis')) {
    return 'treatment-planning';
  }
  if (title.includes('form') || title.includes('consent') || title.includes('agreement')) {
    return 'forms';
  }
  return 'other';
}

/**
 * Infer supervision Task form subtype from Questionnaire title/name (aligned with
 * Task codings in useMissingDocumentsFromMedplum).
 */
export function questionnaireToMissingDocumentFormType(
  questionnaire: Questionnaire | null,
): MissingDocumentFormType | undefined {
  if (!questionnaire) return undefined;
  const t = `${questionnaire.title ?? ''} ${questionnaire.name ?? ''}`.toLowerCase();
  if (/\broi\b|release of information/.test(t)) return 'roi';
  if (t.includes('custody')) return 'custody-agreement';
  if (t.includes('discharge')) return 'discharge-summary';
  if (t.includes('intake') && !t.includes('note') && !t.includes('session')) return 'intake';
  return undefined;
}
